<script setup lang="ts">
    import { provide, shallowRef } from 'vue';
    import { OrthographicCamera } from 'three';
    import { stopAll } from '@/util/stopHandle';
    import { hipSpineXraySceneContext, updateHipSpineXrayScene } from '@/views/3d-planner/hipSpine/scene/hipSpineXrayScene';
    import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
    import { useHipSpineXRaySceneViewModel } from '@/views/3d-planner/hipSpine/useHipSpineXRaySceneViewModel';
    import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
    import { useHipSpineSyncStore } from '@/views/3d-planner/hipSpine/hipSpineSyncStore';
    import { setupLandmarkInteraction } from '@/views/3d-planner/hipSpine/interaction/landmarkInteraction';
    import { useScene } from '@/lib/threeScene/scene/useScene';
    import { attachRenderingState } from '@/lib/threeScene/scene/attachRenderingState';
    import { LOUPE_SOURCE_KEY, type LoupeSource } from '@/views/3d-planner/hipSpine/loupe/loupeView';
    import HipSpineLoupe from '@/views/3d-planner/hipSpine/components/HipSpineLoupe.vue';

    const store = useHipSpineStore();
    const sceneVm = useHipSpineXRaySceneViewModel();
    const editVm = useHipSpineXRayEditViewModel();
    const sync = useHipSpineSyncStore();
    const isLocked = () => sync.isSaving;

    const loupeSource = shallowRef<LoupeSource | null>(null);
    provide(LOUPE_SOURCE_KEY, loupeSource);

    const { containerRef, canvasRef } = useScene({
        camera: () => new OrthographicCamera(),
        sceneContext: () => hipSpineXraySceneContext(sceneVm.sceneState),
        setup: ({ canvas, container, sceneContext, camera, runtime }) => {
            const stopRendering = attachRenderingState({
                runtime,
                state: sceneContext.state.rendering,
                scene: sceneContext.scene,
                camera,
                container,
                canvas,
            });

            loupeSource.value = { scene: sceneContext.scene, mainCamera: camera, mainCanvas: canvas };

            return stopAll(
                stopRendering,
                updateHipSpineXrayScene(sceneContext),
                setupLandmarkInteraction({ canvas, camera, sceneContext, editVm, store, isLocked }),
                () => {
                    loupeSource.value = null;
                },
            );
        },
    });
</script>

<template>
    <div ref="containerRef" class="xray-viewer">
        <canvas ref="canvasRef" class="xray-viewer__canvas overflow-hidden position-absolute" />
        <HipSpineLoupe />
    </div>
</template>

<style scoped lang="scss">
    .xray-viewer {
        position: relative;
        width: 100%;
        height: 100%;
        background: #000;
        border-radius: 12px;
        overflow: hidden;
    }

    .xray-viewer__canvas {
        // Suppress browser pan/pinch gestures so landmark drags work on touch.
        touch-action: none;
    }
</style>
